import type { EnderecoPayload } from "@/lib/services/assinatura-service"

/**
 * A conta do endereço, sem tela: o que falta, campo por campo, e como dizer isso
 * numa frase.
 *
 * Morava dentro de `components/financeiro/endereco-form.tsx`, junto do formulário
 * do tema antigo. Saiu para cá quando o cartão do split (21/10/2026) passou a
 * pedir o endereço da fatura: a página de vendas do personal desenha os campos
 * com as peças DELA, e importar um componente do painel só para saber se falta o
 * número levaria o formulário antigo inteiro junto. O `endereco-form.tsx`
 * continua exportando os mesmos nomes, então quem já usava não muda.
 */

const ENDERECO_VAZIO: EnderecoPayload = {
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  estado: "",
}

export { ENDERECO_VAZIO }

/** Os campos que a nota fiscal exige. O complemento fica de fora de propósito. */
export type CampoDeEndereco = "cep" | "logradouro" | "numero" | "bairro" | "cidade" | "estado"

/** Como cada campo é chamado numa frase ("Falta preencher o número"). */
export const NOME_DO_CAMPO_DE_ENDERECO: Record<CampoDeEndereco, string> = {
  cep: "o CEP",
  logradouro: "a rua",
  numero: "o número",
  bairro: "o bairro",
  cidade: "a cidade",
  estado: "o estado (UF)",
}

/**
 * O que falta no endereço, campo por campo.
 *
 * Existe porque a conferência era só um `boolean`, e com ele a tela de pagamento
 * só sabia dizer "complete o endereço": quem tinha endereço salvo SEM número via
 * o botão "Gerar o Pix" apagado, um endereço aparentemente completo escrito na
 * tela e nenhuma pista de qual campo faltava. A ordem da lista é a ordem em que
 * os campos aparecem no formulário, para a frase apontar na mesma direção da
 * leitura.
 */
export function faltasDoEndereco(e: EnderecoPayload): CampoDeEndereco[] {
  const faltas: CampoDeEndereco[] = []
  if (e.cep.replace(/\D/g, "").length !== 8) faltas.push("cep")
  if (!e.logradouro?.trim()) faltas.push("logradouro")
  if (!e.numero?.trim()) faltas.push("numero")
  if (!e.bairro?.trim()) faltas.push("bairro")
  if (!e.cidade?.trim()) faltas.push("cidade")
  if (e.estado?.trim().length !== 2) faltas.push("estado")
  return faltas
}

/**
 * As faltas numa frase de gente: "o número", "o CEP e o número", "o CEP, o
 * número e o bairro". Devolve null quando não falta nada.
 */
export function escreverFaltasDoEndereco(faltas: CampoDeEndereco[]): string | null {
  const nomes = faltas.map((c) => NOME_DO_CAMPO_DE_ENDERECO[c])
  if (nomes.length === 0) return null
  if (nomes.length === 1) return nomes[0]
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`
}

export function enderecoEstaValido(e: EnderecoPayload): boolean {
  return faltasDoEndereco(e).length === 0
}
